import { useEffect, useState } from "react";
import PageShell from "@/components/PageShell";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import ProposalDialog from "@/components/ProposalDialog";
import WonCelebrationRibbon from "@/components/WonCelebrationRibbon";

/**
 * Pipeline stages, each carrying a theme-aware `accent` expressed as a CSS
 * variable reference. Reads across the board as a controlled flow:
 *   lead (cold / stone) → qualified (navy accent begins) → proposal (navy
 *   saturates) → negotiation (navy + heat) → won (BRASS — this is the one
 *   governed "recognition" state on the board) → lost (destructive, dimmed).
 *
 * We source `won` from `--brass` because a won opportunity is exactly the
 * kind of approved/recognition moment brass exists for.  Every other stage
 * stays on neutral chart tokens so brass keeps its meaning.
 */
const STAGES = [
  { key: "lead",        label: "Lead",        accent: "hsl(var(--chart-3))",       tint: "hsl(var(--chart-3) / 0.05)" },
  { key: "qualified",   label: "Qualified",   accent: "hsl(var(--chart-2))",       tint: "hsl(var(--chart-2) / 0.05)" },
  { key: "proposal",    label: "Proposal",    accent: "hsl(var(--chart-2))",       tint: "hsl(var(--chart-2) / 0.08)" },
  { key: "negotiation", label: "Negotiation", accent: "hsl(var(--chart-2))",       tint: "hsl(var(--chart-2) / 0.12)" },
  { key: "won",         label: "Won",         accent: "hsl(var(--brass))",         tint: "hsl(var(--brass) / 0.10)" },
  { key: "lost",        label: "Lost",        accent: "hsl(var(--destructive))",   tint: "hsl(var(--destructive) / 0.05)" },
];

const fmt$ = (n) => n >= 1000000 ? `$${(n/1000000).toFixed(1)}M` : n >= 1000 ? `$${(n/1000).toFixed(0)}K` : `$${Math.round(n||0)}`;

export default function Pipeline() {
  const [opps, setOpps] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [dragging, setDragging] = useState(null);
  const [proposalOpp, setProposalOpp] = useState(null);
  // Persistent "pinned" selection for talk-through moments — same pattern as
  // Tasks. Click a card to pin, click again (or ESC) to release.
  const [selectedId, setSelectedId] = useState(null);
  // `wonEvent` bundles the deal that just transitioned to Won. Each new event
  // gets a fresh `firedAt` so the ribbon re-animates even if the same deal
  // moves out and back in during a session.
  const [wonEvent, setWonEvent] = useState(null);
  const [form, setForm] = useState({ name: "", client_id: "", client_name: "", stage: "lead", value: "", probability: 20, expected_close: "", source: "", owner: "", next_step: "", notes: "" });

  const load = () => {
    api.get("/opportunities").then((r) => setOpps(r.data));
    api.get("/clients").then((r) => setClients(r.data));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setSelectedId(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const submit = async () => {
    if (!form.name) return toast.error("Name required");
    const client_name = form.client_id ? (clients.find(c => c.id === form.client_id)?.name || form.client_name) : form.client_name;
    await api.post("/opportunities", { ...form, client_name, client_id: form.client_id || null, value: form.value ? parseFloat(form.value) : null, probability: parseInt(form.probability) || 20 });
    setForm({ name: "", client_id: "", client_name: "", stage: "lead", value: "", probability: 20, expected_close: "", source: "", owner: "", next_step: "", notes: "" });
    setOpen(false);
    toast.success("Opportunity added");
    load();
  };

  const moveOpp = async (opp, newStage) => {
    if (opp.stage === newStage) return;
    const probability = newStage === "won" ? 100 : newStage === "lost" ? 0 : opp.probability;
    setOpps((prev) => prev.map(o => o.id === opp.id ? { ...o, stage: newStage, probability } : o));
    // Fire the celebration ribbon ONLY on the transition into Won (never on
    // Won→Won, and never for Lost or any other stage). This is the single
    // sanctioned Brass "recognition" moment on the board.
    if (newStage === "won") {
      setWonEvent({ name: opp.name, value: opp.value || 0, firedAt: Date.now() });
    }
    await api.put(`/opportunities/${opp.id}`, { stage: newStage, probability });
  };

  const totalWeighted = opps.filter(o => !["won","lost"].includes(o.stage)).reduce((sum, o) => sum + (o.value || 0) * (o.probability / 100), 0);
  const wonValue = opps.filter(o => o.stage === "won").reduce((sum, o) => sum + (o.value || 0), 0);

  return (
    <PageShell size="board">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[0.625rem] font-mono uppercase tracking-[0.2em] text-eyebrow">Growth</div>
          <h1 className="mt-2 font-display text-3xl font-bold tracking-tight">Pipeline</h1>
          <p className="text-sm text-muted-foreground mt-1 flex gap-6">
            <span>Weighted <span className="text-foreground font-mono data-cell ml-1">{fmt$(totalWeighted)}</span></span>
            <span>Won this period <span className="text-brass font-mono data-cell ml-1">{fmt$(wonValue)}</span></span>
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-opp-btn" className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90"><Plus size={14} weight="bold" className="mr-2" /> New opportunity</Button></DialogTrigger>
          <DialogContent className="rounded-none bg-card max-w-lg">
            <DialogHeader><DialogTitle>New opportunity</DialogTitle></DialogHeader>
            <Input data-testid="opp-name" placeholder="Opportunity name" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} className="rounded-none bg-secondary/40" />
            <Select value={form.client_id || "none"} onValueChange={(v)=>setForm({...form,client_id: v === "none" ? "" : v})}>
              <SelectTrigger className="rounded-none bg-secondary/40"><SelectValue placeholder="Existing client (optional)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">(New / prospect)</SelectItem>
                {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {!form.client_id && <Input placeholder="Prospect/client name (if new)" value={form.client_name} onChange={(e)=>setForm({...form,client_name:e.target.value})} className="rounded-none bg-secondary/40" />}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input type="number" placeholder="Value ($)" value={form.value} onChange={(e)=>setForm({...form,value:e.target.value})} className="rounded-none bg-secondary/40" />
              <Input type="number" min="0" max="100" placeholder="Probability (%)" value={form.probability} onChange={(e)=>setForm({...form,probability:e.target.value})} className="rounded-none bg-secondary/40" />
              <Input type="date" value={form.expected_close} onChange={(e)=>setForm({...form,expected_close:e.target.value})} className="rounded-none bg-secondary/40" />
              <Input placeholder="Source" value={form.source} onChange={(e)=>setForm({...form,source:e.target.value})} className="rounded-none bg-secondary/40" />
              <Input placeholder="Owner" value={form.owner} onChange={(e)=>setForm({...form,owner:e.target.value})} className="rounded-none bg-secondary/40 col-span-2" />
              <Input placeholder="Next step" value={form.next_step} onChange={(e)=>setForm({...form,next_step:e.target.value})} className="rounded-none bg-secondary/40 col-span-2" />
            </div>
            <Textarea placeholder="Notes" rows={3} value={form.notes} onChange={(e)=>setForm({...form,notes:e.target.value})} className="rounded-none bg-secondary/40" />
            <DialogFooter><Button data-testid="opp-submit" onClick={submit} className="rounded-none bg-primary text-primary-foreground">Add</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
        {STAGES.map((s) => {
          const colOpps = opps.filter(o => o.stage === s.key);
          const stageValue = colOpps.reduce((sum, o) => sum + (o.value || 0), 0);
          const isWon = s.key === "won";
          const isLost = s.key === "lost";
          return (
            <div
              key={s.key}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => { if (dragging) { moveOpp(dragging, s.key); setDragging(null); } }}
              className="border border-border bg-card min-h-[400px] relative"
              style={{
                // Subtle theme-aware column tint that fades down the column
                // so the eye can trace flow without cells feeling striped.
                backgroundImage: `linear-gradient(to bottom, ${s.tint}, transparent 55%)`,
                // Top rail carries the stage's accent as the only bold moment.
                borderTop: `2px solid ${s.accent}`,
                // Muted lost column so a growing "lost" pile doesn't dominate.
                opacity: isLost ? 0.85 : 1,
              }}
              data-testid={`pipeline-col-${s.key}`}
            >
              <div
                className="px-3 py-2 border-b border-border sticky top-0 backdrop-blur-sm"
                style={{ background: `hsl(var(--card))` }}
              >
                <div className="flex items-center justify-between">
                  <div
                    className="text-[0.625rem] font-mono uppercase tracking-widest"
                    style={{ color: s.accent }}
                  >
                    {s.label}
                  </div>
                  <div className="text-[0.625rem] font-mono text-foreground tabular-nums">{colOpps.length}</div>
                </div>
                <div className="text-[0.625rem] font-mono text-muted-foreground mt-0.5">{fmt$(stageValue)}</div>
              </div>
              <div className="p-2 space-y-2">
                {colOpps.map((o) => {
                  const isDragging = dragging?.id === o.id;
                  const isSelected = selectedId === o.id;
                  const toggleSelect = () => setSelectedId(prev => prev === o.id ? null : o.id);
                  return (
                  <div
                    key={o.id}
                    draggable
                    tabIndex={0}
                    role="button"
                    aria-pressed={isSelected}
                    onDragStart={() => setDragging(o)}
                    onDragEnd={() => setDragging(null)}
                    onClick={toggleSelect}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleSelect(); } }}
                    className={`kanban-card p-3 cursor-grab active:cursor-grabbing ${isDragging ? "is-dragging" : ""} ${isSelected ? "is-selected" : ""}`}
                    // Only override the LEFT rail so it carries the stage accent;
                    // the base surface + hover + focus + drag states come from
                    // the shared `.kanban-card` class in index.css so behavior
                    // stays consistent across Tasks and Pipeline boards.
                    style={{ borderLeft: `2px solid ${s.accent}` }}
                    data-testid={`opp-card-${o.id}`}
                    data-selected={isSelected ? "true" : "false"}
                  >
                    <div className="text-sm font-medium leading-tight text-foreground">{o.name}</div>
                    <div className="text-[0.625rem] font-mono uppercase text-muted-foreground tracking-widest mt-0.5 truncate">{o.client_name || "—"}</div>
                    <div className="mt-2 flex items-center justify-between">
                      <span
                        className="font-mono text-xs data-cell tabular-nums"
                        style={{ color: isWon ? "hsl(var(--brass))" : "hsl(var(--foreground))" }}
                      >
                        {fmt$(o.value || 0)}
                      </span>
                      <span className="text-[0.625rem] font-mono text-muted-foreground">{o.probability}%</span>
                    </div>
                    {o.next_step && <div className="mt-2 text-[0.6875rem] text-muted-foreground border-t border-border/50 pt-2 line-clamp-2">→ {o.next_step}</div>}
                    <button
                      onClick={(e) => { e.stopPropagation(); setProposalOpp(o); }}
                      className="mt-2 w-full text-[0.625rem] font-mono uppercase tracking-widest border border-primary/50 text-primary py-1 hover:bg-primary/10 flex items-center justify-center gap-1"
                      data-testid={`opp-proposal-${o.id}`}
                    >
                      <Sparkle size={10} weight="fill" /> {o.proposal_draft ? "View proposal" : "Draft proposal"}
                    </button>
                  </div>
                  );
                })}
                {colOpps.length === 0 && <div className="text-[0.625rem] font-mono uppercase text-muted-foreground text-center py-8">Empty</div>}
              </div>
            </div>
          );
        })}
      </div>

      <ProposalDialog
        opportunity={proposalOpp}
        open={!!proposalOpp}
        onOpenChange={(v) => { if (!v) setProposalOpp(null); }}
        onPromoted={() => load()}
      />
      <WonCelebrationRibbon event={wonEvent} onDismiss={() => setWonEvent(null)} />
    </PageShell>
  );
}
